import type { EnvironmentDto } from '@tms/shared';
import { MonitorCog, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Menu,
  MenuItem,
  MenuLabel,
  SkeletonRows,
} from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { EnvironmentFormDialog } from './EnvironmentFormDialog';
import { useDeleteEnvironment, useEnvironments } from './api';

/** An environment has no colour of its own: a neutral chip, like a key. */
const chipClassName =
  'inline-flex max-w-48 items-center truncate rounded-md border border-border bg-sunken px-1.5 py-0.5 ' +
  'text-[12px] font-medium text-ink outline-none ring-offset-1 ring-offset-surface ' +
  'transition-colors hover:border-border-strong focus-visible:ring-2 focus-visible:ring-accent';

/** Environment administration for a project: add, rename, remove. */
export const EnvironmentsCard = ({ projectCode }: { projectCode: string }) => {
  const { t } = useT();
  const { can } = usePermissions();
  const { errorText } = useErrorText();
  const { data: environments, isPending } = useEnvironments(projectCode);
  const remove = useDeleteEnvironment(projectCode);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EnvironmentDto | undefined>(undefined);
  const [deleting, setDeleting] = useState<EnvironmentDto | undefined>(undefined);

  const canManage = can('environment:manage');

  const openCreate = (): void => {
    setEditing(undefined);
    setFormOpen(true);
  };

  const openEdit = (environment: EnvironmentDto): void => {
    setEditing(environment);
    setFormOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(t('environments.deleted', { name: deleting.name }));
      setDeleting(undefined);
    } catch (error) {
      toast.error(errorText(error, 'environments.deleteFailed'));
    }
  };

  const isUsed = (environment: EnvironmentDto): boolean =>
    environment.caseCount + environment.checklistCount + environment.runCount > 0;

  return (
    <>
      <Card>
        <CardHeader
          title={t('environments.title')}
          description={t('environments.hint')}
          action={
            canManage ? (
              <Button size="sm" onClick={openCreate}>
                <Plus className="size-3.5" />
                {t('environments.new')}
              </Button>
            ) : null
          }
        />
        <CardBody>
          {isPending ? (
            <SkeletonRows rows={2} className="p-0" />
          ) : (environments ?? []).length === 0 ? (
            <p className="flex items-center gap-2 text-[13px] text-ink-subtle">
              <MonitorCog className="size-3.5" />
              {t('environments.empty')}
            </p>
          ) : (
            /* Chips that wrap, like the labels above: a long list stays two or
               three lines, and each chip opens what can be done with it. */
            <ul className="flex flex-wrap gap-1.5">
              {(environments ?? []).map((environment) => (
                <li key={environment.id}>
                  {canManage ? (
                    <Menu
                      align="start"
                      trigger={
                        <button
                          type="button"
                          aria-label={t('environments.actionsFor', { name: environment.name })}
                          className={chipClassName}
                        >
                          {environment.name}
                        </button>
                      }
                    >
                      <MenuLabel>
                        {isUsed(environment)
                          ? t('environments.usage', {
                              cases: environment.caseCount,
                              checklists: environment.checklistCount,
                              runs: environment.runCount,
                            })
                          : t('environments.notUsed')}
                      </MenuLabel>
                      <MenuItem onSelect={() => openEdit(environment)}>
                        <Pencil className="size-3.5" />
                        {t('common.edit')}
                      </MenuItem>
                      <MenuItem destructive onSelect={() => setDeleting(environment)}>
                        <Trash2 className="size-3.5" />
                        {t('common.delete')}
                      </MenuItem>
                    </Menu>
                  ) : (
                    <span className={chipClassName}>{environment.name}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <EnvironmentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        projectCode={projectCode}
        environment={editing}
      />

      <ConfirmDialog
        open={deleting !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleting(undefined);
        }}
        title={t('environments.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('environments.deleteHint', {
          cases: deleting?.caseCount ?? 0,
          checklists: deleting?.checklistCount ?? 0,
          runs: deleting?.runCount ?? 0,
        })}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
